<script setup lang="ts">
import { ref } from 'vue'

import cn from 'internal/tailwind-classmerge'

import { CloseIcon } from '@fond-of/satch-icons'

defineProps<{
  chatItems: ({
    __typename?: 'NavigationItem'
    id: string
    internalTitle?: string | null
    label?: string | null
    url?: string | null
    gtmDataAttr?: string | null
    gtmDataLabel?: string | null
    menuIcon?: {
      __typename?: 'Asset'
      url?: string | null
    } | null
  } | null)[]
}>()

declare global {
  interface Window {
    zE?: {
      (type: 'messenger:on', command: 'open', callback: () => void): void
      (type: 'messenger:on', command: 'close', callback: () => void): void
    }
  }
}

const isChatWidgetOpen = ref<boolean>(false)

window.addEventListener('zendeskInit', () => {
  if (typeof window.zE === 'undefined') {
    // for testing purposes
    console.log('Zendesk Chat Widget is not available')
    return
  }
  // for testing purposes
  console.log('Zendesk Chat Widget is available')

  window.zE?.('messenger:on', 'open', () => {
    isChatWidgetOpen.value = true
  })

  window.zE?.('messenger:on', 'close', () => {
    isChatWidgetOpen.value = false
  })
})
</script>

<template>
  <div
    id="chatWidget"
    ref="chatContainer"
    :class="
      cn(
        'z-chat fixed bottom-[10px] right-2 hidden overflow-hidden rounded-[50px] p-3 transition-all duration-500 md:bottom-12 md:right-6',
        { 'bg-primary-light max-h-[280px] shadow-2xl': isChatWidgetOpen },
        { 'max-h-[85px] bg-transparent': !isChatWidgetOpen }
      )
    "
  >
    <ul class="w-15 relative">
      <li
        v-show="!isChatWidgetOpen"
        class="absolute left-0 top-0 z-10 h-full w-full cursor-pointer"
        @click="isChatWidgetOpen = true"
      />
      <li
        v-for="item in chatItems"
        :key="item?.id"
        :class="
          cn('pointer-events-auto mb-2 touch-auto', {
            'cursor-pointer': !isChatWidgetOpen,
          })
        "
      >
        <a
          :href="item?.url ?? undefined"
          :gtm-data-attr="item?.gtmDataAttr"
          :gtm-data-label="item?.gtmDataLabel"
          :class="{ 'pointer-events-none touch-none': !isChatWidgetOpen }"
        >
          <img
            v-if="item?.menuIcon?.url"
            class="mb-0.5 w-full"
            :src="item?.menuIcon?.url"
            :alt="item?.label ?? ''"
          />
          <span
            :class="
              cn(
                'block w-full whitespace-nowrap text-center text-[12px] transition-opacity duration-300',
                {
                  'opacity-0': !isChatWidgetOpen,
                  'opacity-100': isChatWidgetOpen,
                }
              )
            "
          >
            {{ item?.label }}
          </span>
        </a>
      </li>
      <li
        id="closeChatWidget"
        class="cursor-pointer"
        @click="isChatWidgetOpen = false"
      >
        <CloseIcon
          :class="
            cn(
              'text-primary close-icon w-full transition-transform delay-75 duration-300',
              { 'scale-50': isChatWidgetOpen, 'scale-0': !isChatWidgetOpen }
            )
          "
        />
      </li>
    </ul>
  </div>
</template>

<style>
.close-icon {
  path {
    stroke-width: 4;
  }
}
</style>
